import {
  LIVE_UPDATES_PATH,
  OLO_WEBHOOK_SETUP_PARAM,
  OLO_WEBHOOK_SETUP_VALUE,
} from 'constants/liveUpdates'

// Single owner of the RecoveryAI route literals. Co-locating them here keeps
// the post-test ("Save and test") push target in ONE place: the Settings
// `useSaveAndTest` hook and the Agent-Tuning config modals both import
// AGENT_TUNING_PATH instead of each carrying a private copy, so the route can be
// renamed in one edit and the panes can never split-brain onto different paths.
export const RECOVERY_AI_ROOT = '/recovery_ai'

// The Agent Tuning tab is the R12 home of the live simulator; Save-and-test
// routes here on a successful save (replaces the legacy `/recovery_ai/simulator`
// alias).
export const AGENT_TUNING_PATH = `${RECOVERY_AI_ROOT}/agent_tuning`

// The Escalations tab — the recovery-case queue, and the deep-link target for
// every Home surface that routes an operator into RecoveryAI (the Needs
// Attention row and the Home feedback tiles). Owned here rather than re-typed
// at each call site: Landing's TAB_PATHS and activeTabFromPath both key off it,
// so a rename that missed one of them would leave the tab bar pointing at a
// path the tab resolver no longer recognises.
export const ESCALATIONS_PATH = `${RECOVERY_AI_ROOT}/escalations`

// The Settings tab (the vertical sub-tab hub). Keeps the legacy
// `/recovery_ai/configuration` path so existing links + the wizard's
// post-completion redirect keep working.
export const SETTINGS_PATH = `${RECOVERY_AI_ROOT}/configuration`

// Entry point of the onboarding wizard (its `quick-start` intro stage). Two CTAs
// push here — GetStarted (first-time) and SetupGuidePane's re-open button (AI-580
// re-entry) — so the literal is owned here rather than copied into each.
export const WIZARD_QUICK_START_PATH = `${RECOVERY_AI_ROOT}/configure/quick-start`

// Deep-link straight to a Settings sub-tab. The fragment is the InnerTabs
// sub-tab key (see SETTINGS_TAB_KEYS in ./Settings) — InnerTabs reads the URL
// hash on mount to pick the active sub-tab. Used by the Overview billboard CTA
// ("Turn on RecoveryAI at more locations") to send merchants to the Active
// Locations pane.
export const ACTIVE_LOCATIONS_SETTINGS_PATH = `${SETTINGS_PATH}#active_locations`

// Deep-link straight to the Brand voice sub-tab (same hash mechanism as above).
export const BRAND_VOICE_SETTINGS_PATH = `${SETTINGS_PATH}#brand_voice`

// AI-303 (FR-41): deep-link straight to the Suspected abuse review sub-tab
// (mirrors ACTIVE_LOCATIONS_SETTINGS_PATH). For any escalation-email / Overview
// CTA that links an operator to the gaming-review queue; the routes.test.ts
// drift guard keeps the hash aligned with SETTINGS_TAB_KEYS.
export const GAMING_REVIEW_SETTINGS_PATH = `${SETTINGS_PATH}#gaming_review`

// WP7 Activation workflow (AI-39). The configured-summary page lives at
// `/recovery_ai/activation`; the post-activation success page nests beneath it.
// Distinct from `configure` (the wizard) and `configuration` (Settings).
export const ACTIVATION_PATH = `${RECOVERY_AI_ROOT}/activation`
export const ACTIVATION_SUCCESS_PATH = `${ACTIVATION_PATH}/success`

// AI-892: ConfiguredSummary sends a config editor into the wizard Testing step to
// preview the sandbox, tagging the URL with `?returnTo=activation`. The wizard
// reads it so Cancel returns to the activation summary instead of falling through
// to push('/recovery_ai') — which, for a configured-but-off merchant, lands on the
// promo page (Landing's activation redirect sits behind the promo gate and can't
// fire), the dead end this ticket removes. Owned here so the writer and reader
// can't drift onto different literals.
export const WIZARD_RETURN_TO_PARAM = 'returnTo'
export const WIZARD_RETURN_TO_ACTIVATION = 'activation'

// AI-1295 (FR-39), design revision: the RecoveryAI Overview reuses the Live
// updates OloWebhookBanner (via src/components/OloWebhookBanner) rather than
// its own alert. Its CTA deep-links to Live updates with a query param that
// opens the "Connect Olo webhooks" modal on arrival, so the merchant doesn't
// land on a page with the same banner and the same button to click again.
// Composed from constants/liveUpdates.ts (the shared owner of the path/param/
// value literals — RecoveryAI can't import LiveUpdates/index.constants.ts
// directly across the scene boundary, so the literals live in a
// scene-independent location instead of being repeated here).
export const LIVE_UPDATES_OLO_SETUP_PATH = `${LIVE_UPDATES_PATH}?${OLO_WEBHOOK_SETUP_PARAM}=${OLO_WEBHOOK_SETUP_VALUE}`
